import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@volca/ui/lib/toast";
import { isInvalidPasswordResetCodeError, useAuth } from "@/core/auth";

interface UseWelcomeLinkValidationOptions {
  email: string;
  verificationCode: string;
  enabled: boolean;
}

export const useWelcomeLinkValidation = ({
  email,
  verificationCode,
  enabled,
}: UseWelcomeLinkValidationOptions) => {
  const { forgotPassword, verifyResetPasswordCode } = useAuth();
  const navigate = useNavigate();
  const validationKey = enabled
    ? JSON.stringify([email, verificationCode])
    : null;
  const [validatedKey, setValidatedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!validationKey) return;

    const abortController = new AbortController();

    const recoverFromRejectedLink = async () => {
      try {
        await forgotPassword({ email }, { signal: abortController.signal });
        toast.success("A new setup code was sent", {
          description:
            "Your original welcome link expired or is no longer valid. Please check your email for the new code.",
          duration: 10000,
        });
      } catch {
        if (abortController.signal.aborted) return;

        toast.error("We couldn't send a new setup code", {
          description:
            "Please enter your email to try again and continue setting up your account.",
        });
        void navigate("/signup");
      }
    };

    const validateWelcomeLink = async () => {
      if (!email) {
        toast.error("This welcome link is missing your email address", {
          description: "Please enter your email to receive a new setup code.",
        });
        void navigate("/signup");
        return;
      }

      if (!verificationCode) {
        await recoverFromRejectedLink();
        return;
      }

      try {
        await verifyResetPasswordCode(
          {
            email,
            verification_code: verificationCode,
          },
          { signal: abortController.signal },
        );
        if (!abortController.signal.aborted) {
          setValidatedKey(validationKey);
        }
      } catch (error) {
        if (abortController.signal.aborted) return;

        if (isInvalidPasswordResetCodeError(error)) {
          await recoverFromRejectedLink();
          return;
        }

        toast.error("We couldn't validate your welcome link", {
          description:
            "Please enter your email to continue setting up your account.",
        });
        void navigate("/signup");
      }
    };

    void validateWelcomeLink();
    return () => abortController.abort();
  }, [
    email,
    enabled,
    forgotPassword,
    navigate,
    validationKey,
    verificationCode,
    verifyResetPasswordCode,
  ]);

  return validationKey !== null && validatedKey !== validationKey;
};
